<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">Positionen</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('positions')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <button
                        :aria-label="$t('Filter menu')"
                        type="button"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        @click="toggleFilterSidebar"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                        <span
                            v-if="hasActiveFilters"
                            aria-hidden="true"
                            class="stage-filter-active-dot"
                        ></span>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="getPositions"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow rounded-md relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto sm:overflow-hidden">
                        <template v-if="paginatedPositions.length > 0">
                            <div class="hidden sm:flex stage-listview-split stage-listview-split-keep-last-border">
                                <div class="stage-listview-split-header">
                                    <table class="stage-listview-split-table position-list-split-table table-listview table-listview--compact-y">
                                        <colgroup>
                                            <col class="w-[3.25rem]">
                                            <col class="w-[300px]">
                                            <col class="w-[18rem]">
                                            <col class="w-[10rem]">
                                            <col class="w-[13rem]">
                                            <col class="w-[12rem]">
                                            <col class="w-28">
                                        </colgroup>
                                        <thead>
                                        <tr>
                                            <th
                                                class="table-listview-l-th w-[3.25rem] min-w-[3.25rem] max-w-[3.25rem] px-0 whitespace-nowrap text-center border-r border-ultralight"
                                                style="overflow: visible; text-overflow: clip;"
                                            >
                                                ID
                                            </th>
                                            <th class="table-listview-l-th w-[300px] min-w-[300px]" style="overflow: visible; text-overflow: clip;">Titel</th>
                                            <th class="table-listview-l-th w-[18rem]" style="overflow: visible; text-overflow: clip;">Fortschritt</th>
                                            <th class="table-listview-l-th w-[10rem]" style="overflow: visible; text-overflow: clip;">Freigabestatus</th>
                                            <th class="table-listview-l-th w-[13rem]" style="overflow: visible; text-overflow: clip;">Erstellt von</th>
                                            <th class="table-listview-l-th w-[12rem]" style="overflow: visible; text-overflow: clip;">Letztes Update</th>
                                            <th class="table-listview-l-th table-listview-l-th-right w-28 min-w-[7rem]" style="overflow: visible; text-overflow: clip;">{{ $t('Actions') }}</th>
                                        </tr>
                                        </thead>
                                    </table>
                                </div>
                                <div class="stage-listview-split-body position-list-split-body">
                                    <table class="stage-listview-split-table position-list-split-table table-listview table-listview--zebra table-listview--compact-y">
                                        <colgroup>
                                            <col class="w-[3.25rem]">
                                            <col class="w-[300px]">
                                            <col class="w-[18rem]">
                                            <col class="w-[10rem]">
                                            <col class="w-[13rem]">
                                            <col class="w-[12rem]">
                                            <col class="w-28">
                                        </colgroup>
                                        <tbody class="bg-white">
                                        <template v-for="position in paginatedPositions" :key="position.id">
                                            <tr
                                                class="cursor-pointer"
                                                @click="$router.push(positionDetailPath(position.id))"
                                            >
                                                <td
                                                    class="table-listview-l-td align-middle whitespace-no-wrap w-[3.25rem] min-w-[3.25rem] max-w-[3.25rem] px-0 py-0 text-center border-r border-ultralight"
                                                    style="background-color: var(--table-listview-zebra-color);"
                                                >
                                                    <div class="flex items-center justify-center h-full min-h-[4.5rem] px-1">
                                                        <span class="text-xs leading-4 text-gray-400 font-medium">{{ position.id }}</span>
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap w-[300px] min-w-[300px] position-list-title-cell">
                                                    <span
                                                        v-if="positionPinningAvailable && position.is_pinned"
                                                        class="position-pin-marker"
                                                        title="Angepinnt"
                                                    >
                                                        <svg-vue class="position-pin-marker-icon" icon="font-awesome.regular.thumbtack"></svg-vue>
                                                    </span>
                                                    <div :class="['position-title-cell-content', positionPinningAvailable && position.is_pinned ? 'position-title-cell-content--pinned' : '']">
                                                        <div v-if="getPositionProjects(position).length > 0" class="flex items-center flex-shrink-0">
                                                            <template v-for="(project, projectIndex) in getPositionProjects(position)" :key="'customer-position-project-' + position.id + '-' + project.id">
                                                                <img
                                                                    :src="positionProjectImageUrl(project)"
                                                                    :alt="project.title || 'Projektbild'"
                                                                    :title="positionProjectTooltip(project)"
                                                                    :class="projectIndex === 0 ? '' : '-ml-7'"
                                                                    class="h-9 w-9 rounded-sm object-cover border border-white"
                                                                >
                                                            </template>
                                                        </div>
                                                        <div :class="getPositionProjects(position).length > 0 ? 'ml-3' : ''" class="min-w-0 flex-1">
                                                        <div class="text-sm font-medium text-gray-700 truncate">
                                                            {{ position.title }}
                                                        </div>
                                                        <div class="text-sm text-gray-500 truncate max-w-xl">
                                                            {{ getBodyPreview(position.body_excerpt) }}
                                                        </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap w-[18rem]">
                                                    <div class="text-sm text-gray-700 font-medium inline-flex items-center">
                                                        <span>{{ getCurrentProcessStepLabel(position) }}</span>
                                                        <app-tooltip
                                                            v-if="hasProcessStepsTooltip(position)"
                                                            class="ml-2"
                                                            width-class="w-max max-w-[22rem]"
                                                            placement="bottom"
                                                        >
                                                            <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.circle-info"></svg-vue>
                                                            <template #content>
                                                                <div class="px-3 py-2">
                                                                    <div
                                                                        v-for="(processStep, processStepIndex) in getProcessStepsForTooltip(position)"
                                                                        :key="'position-customer-process-step-tooltip-' + position.id + '-' + processStep.id"
                                                                        class="position-process-step-tooltip-item"
                                                                    >
                                                                        <span
                                                                            class="position-process-step-tooltip-dot"
                                                                            style="width: 10px; height: 10px;"
                                                                            :style="{ backgroundColor: getProcessStepDotColor(position, processStep, processStepIndex) }"
                                                                        ></span>
                                                                        <span
                                                                            :title="processStep.name"
                                                                            class="position-process-step-tooltip-text"
                                                                        >
                                                                            {{ processStep.name }}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </template>
                                                        </app-tooltip>
                                                    </div>
                                                    <div class="mt-1 w-72 max-w-full">
                                                        <div class="h-1.5 bg-gray-300 rounded-full overflow-hidden">
                                                            <div
                                                                class="h-full rounded-full"
                                                                :style="{ width: getPositionProgressWidth(position) + '%', backgroundColor: getCurrentProcessStepColor(position) }"
                                                            ></div>
                                                        </div>
                                                        <div class="mt-1 text-xs text-gray-500">
                                                            {{ getDisplayProcessStepProgressDone(position) }} / {{ position.process_steps_count || 0 }}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap w-[10rem]">
                                                    <span
                                                        v-if="hasOpenApprovalRequests(position)"
                                                        class="inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium bg-red-50 text-red-700 border-red-200"
                                                    >
                                                        angefragt
                                                    </span>
                                                    <span
                                                        v-else
                                                        class="text-sm text-gray-500"
                                                    >
                                                        freigegeben
                                                    </span>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap w-[13rem]">
                                                    <div class="text-sm text-gray-700 truncate inline-flex items-center">
                                                        <img
                                                            :src="getUserAvatar(position)"
                                                            :alt="getPositionAuthorName(position) || 'Avatar'"
                                                            class="h-6 w-6 rounded-full mr-2 object-cover flex-shrink-0"
                                                        >
                                                        {{ getPositionAuthorName(position) || '-' }}
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap w-[12rem]">
                                                    <div class="text-sm text-gray-500">
                                                        {{ $momentFormatDateTimeAgo(position.updated_at) }}
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td w-28 min-w-[7rem] text-right relative" @click.stop>
                                                    <div class="inline-flex items-center justify-end w-full relative">
                                                        <button
                                                            type="button"
                                                            class="inline-flex items-center justify-center h-7 w-7 text-gray-500"
                                                            @click.stop.prevent="toggleRowActions(position.id)"
                                                        >
                                                            <span class="flex flex-col items-center justify-center space-y-0.5">
                                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                            </span>
                                                        </button>
                                                        <div
                                                            v-if="activeActionsPositionId === position.id"
                                                            class="absolute right-0 top-9 z-30 w-56 rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-sm"
                                                        >
                                                            <button
                                                                type="button"
                                                                class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                                @click.stop.prevent="openPosition(position)"
                                                            >
                                                                {{ $t('Open position') }}
                                                            </button>
                                                            <button
                                                                v-if="positionPinningAvailable"
                                                                type="button"
                                                                class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                                @click.stop.prevent="togglePositionPin(position)"
                                                            >
                                                                {{ position.is_pinned ? 'Position lösen' : 'Position anpinnen' }}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                                @click.stop.prevent="openPositionInNewWindow(position)"
                                                            >
                                                                {{ $t('Open in new window') }}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </template>

                        <template v-else-if="!loading">
                            <empty-state />
                        </template>
                    </div>

                    <aside
                        :aria-hidden="!filterSidebarOpen"
                        class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-20"
                        :class="filterSidebarOpen ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                    >
                        <div class="stage-filter-body">
                            <div class="stage-filter-title-row">
                                <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                                <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                            </div>
                            <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                            <div class="stage-filter-row">
                                <button
                                    class="stage-filter-sort-btn text-sm leading-5 font-medium"
                                    type="button"
                                    @click="toggleSortOrder"
                                >
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                                </button>
                                <div class="stage-filter-sort-select-wrap">
                                    <select
                                        v-model="sort.column"
                                        aria-label="Sort by"
                                        class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                    >
                                        <option value="id">ID</option>
                                        <option value="title">Titel</option>
                                        <option value="process_step">Status</option>
                                        <option value="author">Erstellt von</option>
                                        <option value="updated_at">Letztes Update</option>
                                    </select>
                                    <span class="stage-filter-select-icon">
                                        <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                    </span>
                                </div>
                            </div>
                            <div class="stage-filter-label mt-4">{{ $t('Search') }}</div>
                            <div class="mb-4">
                                <div class="relative">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                    </div>
                                    <input
                                        v-model="filters.search"
                                        :placeholder="$t('Search')"
                                        class="stage-filter-input form-input block pl-10 text-sm transition ease-in-out duration-150"
                                        @input="page = 1"
                                    >
                                </div>
                            </div>
                            <div class="stage-filter-label mt-4">Einträge pro Seite</div>
                            <div class="mb-4">
                                <input-select
                                    id="positions-customer-per-page"
                                    v-model="perPage"
                                    class="stage-filter-select"
                                    :options="[{id: 10, name: 10}, {id: 25, name: 25}, {id: 50, name: 50}, {id: 100, name: 100}]"
                                    option-label="name"
                                    required
                                    @change="changePerPage"
                                />
                            </div>
                            <div v-if="hasActiveFilters" class="mt-4">
                                <button
                                    id="positions-customer-filter-reset-btn"
                                    type="button"
                                    class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                    @click="resetFilters"
                                >
                                    {{ $t('Reset selection') }}
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>

                <list-footer-pagination
                    :start="paginationStart"
                    :end="paginationEnd"
                    :total="paginationTotal"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>
    </div>
</template>

<script>
import {UI_COLORS} from '@/utils/ui-colors';
import {
    queryHasKey,
    queryNumber,
    queryNumberArray,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";

export default {
    name: "position-customer-list",
    metaInfo() {
        return {
            title: "Positionen"
        }
    },
    data() {
        const routeQuery = (this.$route && this.$route.query) ? this.$route.query : {};
        const initialProjectFilter = queryNumberArray(
            routeQuery,
            queryHasKey(routeQuery, 'list_projects') ? 'list_projects' : ['projects[]', 'projects'],
            []
        );
        const initialPage = queryNumber(routeQuery, 'list_page', 1);
        const initialPerPage = queryNumber(routeQuery, 'list_per_page', 50, [10, 25, 50, 100]);
        let initialSortColumn = queryString(routeQuery, 'list_sort_column', 'updated_at');
        if (!['id', 'title', 'process_step', 'author', 'updated_at'].includes(initialSortColumn)) {
            initialSortColumn = 'updated_at';
        }
        let initialSortOrder = queryString(routeQuery, 'list_sort_order', 'desc');
        if (!['asc', 'desc'].includes(initialSortOrder)) {
            initialSortOrder = 'desc';
        }

        return {
            loading: true,
            positionList: [],
            serverPagination: {
                currentPage: 1,
                perPage: 50,
                total: 0,
                totalPages: 1,
            },
            filters: {
                search: queryString(routeQuery, 'list_search', ''),
                projects: initialProjectFilter,
            },
            sort: {
                column: initialSortColumn,
                order: initialSortOrder,
            },
            page: initialPage !== null && initialPage > 0 ? initialPage : 1,
            perPage: initialPerPage !== null ? initialPerPage : 50,
            filterSidebarOpen: false,
            activeActionsPositionId: null,
            positionPinningAvailable: false,
            searchRefreshTimeout: null,
        };
    },
    computed: {
        filteredPositions() {
            return Array.isArray(this.positionList) ? this.positionList : [];
        },
        pagination() {
            const total = Number(this.serverPagination?.total || 0);
            const perPage = Number(this.serverPagination?.perPage || this.perPage || 50);
            const totalPages = Math.max(1, Number(this.serverPagination?.totalPages || 1));
            const currentPage = Math.min(
                Math.max(1, Number(this.serverPagination?.currentPage || this.page || 1)),
                totalPages
            );
            return {
                currentPage,
                perPage,
                total,
                totalPages,
            };
        },
        paginatedPositions() {
            return this.filteredPositions;
        },
        paginationStart() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return (this.pagination.currentPage - 1) * this.pagination.perPage + 1;
        },
        paginationEnd() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
        },
        paginationTotal() {
            return this.pagination.total;
        },
        hasActiveFilters() {
            return (this.filters.search || '').trim() !== ''
                || (Array.isArray(this.filters.projects) && this.filters.projects.length > 0)
                || this.sort.column !== 'updated_at'
                || this.sort.order !== 'desc'
                || Number(this.perPage || 0) !== 50;
        },
    },
    watch: {
        'filters.search'() {
            this.scheduleSearchRefresh();
        },
        'filters.projects': {
            handler() {
                this.resetToFirstPageAndRefresh();
            },
            deep: true,
        },
        'sort.column'() {
            this.resetToFirstPageAndRefresh();
        },
        'sort.order'() {
            this.resetToFirstPageAndRefresh();
        },
        perPage() {
            this.resetToFirstPageAndRefresh();
        },
        page() {
            this.getPositions();
        },
    },
    mounted() {
        this.getPositions();
        document.addEventListener('click', this.handleDocumentClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
        if (this.searchRefreshTimeout) {
            clearTimeout(this.searchRefreshTimeout);
            this.searchRefreshTimeout = null;
        }
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    methods: {
        handleDocumentClick() {
            this.activeActionsPositionId = null;
        },
        scheduleSearchRefresh() {
            if (this.searchRefreshTimeout) {
                clearTimeout(this.searchRefreshTimeout);
            }
            this.searchRefreshTimeout = setTimeout(() => {
                this.searchRefreshTimeout = null;
                this.resetToFirstPageAndRefresh();
            }, 250);
        },
        resetToFirstPageAndRefresh() {
            if (this.page !== 1) {
                this.page = 1;
                return;
            }

            this.getPositions();
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
            }
        },
        toggleSortOrder() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
        },
        resetFilters() {
            this.filters.search = '';
            this.filters.projects = [];
            this.sort = {
                column: 'updated_at',
                order: 'desc',
            };
            this.perPage = 50;
            this.page = 1;
        },
        changePerPage() {
            this.page = 1;
        },
        toggleFilterSidebar() {
            this.filterSidebarOpen = !this.filterSidebarOpen;
        },
        toggleRowActions(positionId) {
            this.activeActionsPositionId = this.activeActionsPositionId === positionId ? null : positionId;
        },
        positionDetailPath(positionId) {
            return routeWithReturnTo('/positions/' + positionId, this.$route, '/positions/list');
        },
        openPosition(position) {
            this.activeActionsPositionId = null;
            this.$router.push(this.positionDetailPath(position.id));
        },
        openPositionInNewWindow(position) {
            this.activeActionsPositionId = null;
            window.open(this.positionDetailPath(position.id), '_blank');
        },
        togglePositionPin(position) {
            if (!this.positionPinningAvailable) {
                this.activeActionsPositionId = null;
                return;
            }

            const self = this;
            const wasPinned = Boolean(position?.is_pinned);
            const request = wasPinned
                ? axios.delete('api/positions/' + position.id + '/pin')
                : axios.post('api/positions/' + position.id + '/pin');

            request.then(function (response) {
                position.is_pinned = !wasPinned;
                position.pinned_at = response.data?.pinned_at || null;
                self.activeActionsPositionId = null;
                self.getPositions();
                if (self.$notify) {
                    self.$notify({
                        type: 'success',
                        text: self.$t('Data updated correctly'),
                    });
                }
            });
        },
        getPositions() {
            const self = this;
            self.loading = true;
            self.syncPositionListRouteState();
            axios.get('api/positions', {
                params: {
                    search: self.filters.search,
                    projects: self.filters.projects,
                    page: self.page,
                    perPage: self.perPage,
                    sort: JSON.stringify({
                        column: self.resolveSortColumn(),
                        order: self.sort.order,
                    }),
                },
            }).then(function (response) {
                const payload = response && response.data ? response.data : [];
                const items = Array.isArray(payload)
                    ? payload
                    : (Array.isArray(payload.items) ? payload.items : []);
                const pagination = !Array.isArray(payload) && payload && payload.pagination
                    ? payload.pagination
                    : null;

                self.positionList = items;
                self.positionPinningAvailable = Boolean(payload.position_pinning_available);
                self.serverPagination = {
                    currentPage: Number(pagination?.currentPage || self.page || 1),
                    perPage: Number(pagination?.perPage || self.perPage || 50),
                    total: Number(pagination?.total || items.length || 0),
                    totalPages: Math.max(1, Number(pagination?.totalPages || 1)),
                };
                self.loading = false;
            }).catch(function () {
                self.positionList = [];
                self.serverPagination = {
                    currentPage: 1,
                    perPage: self.perPage,
                    total: 0,
                    totalPages: 1,
                };
                self.loading = false;
            });
        },
        syncPositionListRouteState() {
            const listRouteKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
                'list_projects',
                'projects',
                'projects[]',
            ];
            const nextQuery = queryWithoutKeys(this.$route.query || {}, listRouteKeys);

            setQueryValue(nextQuery, 'list_page', Number(this.page || 1), 1);
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 50), 50);
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column, 'updated_at');
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order, 'desc');
            setQueryValue(nextQuery, 'list_search', this.filters.search, '');
            setQueryValue(nextQuery, 'list_projects', this.filters.projects, []);

            if (sameRouteQuery(this.$route.query || {}, nextQuery)) {
                return;
            }

            const routeReplace = this.$router.replace({
                path: this.$route.path,
                query: nextQuery,
            });

            if (routeReplace && typeof routeReplace.catch === 'function') {
                routeReplace.catch(function () {
                });
            }
        },
        resolveSortColumn() {
            const sortColumnMap = {
                id: 'id',
                title: 'title',
                process_step: 'current_process_step_id',
                author: 'user_id',
                updated_at: 'updated_at',
            };

            return sortColumnMap[this.sort.column] || 'updated_at';
        },
        getPositionProgressWidth(position) {
            const total = Number(position.process_steps_count || 0);
            if (total < 1) {
                return 0;
            }

            const done = this.getDisplayProcessStepProgressDone(position);
            return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
        },
        getDisplayProcessStepProgressDone(position) {
            const processSteps = this.getProcessStepsForTooltip(position);
            if (processSteps.length < 1) {
                return 0;
            }

            const completedCount = Number(position?.completed_process_steps_count || 0);
            return Math.max(0, Math.min(completedCount, processSteps.length));
        },
        getCurrentProcessStepColor(position) {
            const processSteps = this.getProcessStepsForTooltip(position);
            if (processSteps.length < 1) {
                return UI_COLORS.text.default;
            }

            const completedCount = this.getDisplayProcessStepProgressDone(position);
            if (completedCount >= processSteps.length) {
                const lastStep = processSteps[processSteps.length - 1];
                return (lastStep && lastStep.color) ? lastStep.color : UI_COLORS.text.default;
            }

            if (position && position.current_process_step && position.current_process_step.color) {
                return position.current_process_step.color;
            }

            if (completedCount > 0) {
                const lastCompletedIndex = Math.min(completedCount - 1, processSteps.length - 1);
                const lastCompletedStep = processSteps[lastCompletedIndex];
                return (lastCompletedStep && lastCompletedStep.color) ? lastCompletedStep.color : UI_COLORS.text.default;
            }

            return UI_COLORS.text.default;
        },
        getCurrentProcessStepLabel(position) {
            const processSteps = this.getProcessStepsForTooltip(position);
            if (processSteps.length > 0) {
                const firstIncompleteStep = processSteps.find((step) => step && step.is_completed === false);
                if (firstIncompleteStep && firstIncompleteStep.name) {
                    return firstIncompleteStep.name;
                }

                const lastStep = processSteps[processSteps.length - 1];
                if (lastStep && lastStep.name) {
                    return lastStep.name;
                }
            }

            if (position && position.current_process_step && position.current_process_step.name) {
                return position.current_process_step.name;
            }

            return processSteps.length > 0 ? 'Offen' : '-';
        },
        getProcessStepsForTooltip(position) {
            if (!position || !Array.isArray(position.process_steps)) {
                return [];
            }

            return position.process_steps
                .filter((step) => step && step.id && step.name)
                .sort((a, b) => Number(a.value || 0) - Number(b.value || 0));
        },
        hasProcessStepsTooltip(position) {
            return this.getProcessStepsForTooltip(position).length > 0;
        },
        getUserAvatar(position) {
            const user = position && position.user ? position.user : null;
            if (!user) {
                return '';
            }
            const candidates = [
                user.avatar_url,
                user.avatar,
                user.gravatar,
            ];
            const valid = candidates.find((value) => typeof value === 'string' && value.trim() !== '' && value !== 'gravatar');
            return valid || '';
        },
        getPositionAuthorName(position) {
            if (position && typeof position.author === 'string' && position.author.trim() !== '') {
                return position.author.trim();
            }

            const userName = position && position.user && typeof position.user.name === 'string'
                ? position.user.name.trim()
                : '';

            return userName !== '' ? userName : '-';
        },
        hasOpenApprovalRequests(position) {
            const costStatus = this.normalizeApprovalStatus(position?.customer_cost_approval_status);
            const actionStatus = this.normalizeApprovalStatus(position?.customer_action_approval_status);

            return costStatus === 'pending'
                || costStatus === 'adjusted'
                || actionStatus === 'pending'
                || actionStatus === 'adjusted';
        },
        normalizeApprovalStatus(status) {
            const normalized = String(status || '').trim().toLowerCase();
            if (normalized === 'approved' || normalized === 'pending' || normalized === 'adjusted') {
                return normalized;
            }

            return 'none';
        },
        getProcessStepDotColor(position, processStep, processStepIndex) {
            const currentStepId = Number(position?.current_process_step?.id || 0);
            if (currentStepId > 0 && Number(processStep?.id || 0) === currentStepId) {
                return processStep.color || UI_COLORS.text.subtle;
            }

            if (typeof processStep?.is_completed === 'boolean') {
                return processStep.is_completed ? (processStep.color || UI_COLORS.text.subtle) : UI_COLORS.text.subtle;
            }

            const completedCount = this.getDisplayProcessStepProgressDone(position);
            return processStepIndex < completedCount ? (processStep.color || UI_COLORS.text.subtle) : UI_COLORS.text.subtle;
        },
        getBodyPreview(value) {
            if (!value) {
                return '-';
            }

            const stripped = String(value)
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();

            if (!stripped) {
                return '-';
            }

            const decoder = document.createElement('textarea');
            decoder.innerHTML = stripped;

            return (decoder.value || decoder.textContent || '')
                .replace(/\u00a0/g, ' ')
                .replace(/\s+/g, ' ')
                .trim() || '-';
        },
        getPositionProjects(position) {
            const projects = Array.isArray(position?.projects)
                ? position.projects
                : (position?.projects && Array.isArray(position.projects.data) ? position.projects.data : []);
            if (projects.length > 0) {
                return projects.filter((project) => project && Number(project.id) > 0);
            }

            if (position && position.project && Number(position.project.id) > 0) {
                return [position.project];
            }

            return [];
        },
        positionProjectImageUrl(project) {
            if (project && project.image_url) {
                return project.image_url;
            }

            return '/images/default/icon.png';
        },
        positionProjectTooltip(project) {
            const title = String(project?.title || '').trim();
            return title !== '' ? `Projekt: ${title}` : 'Projekt';
        },
    }
}
</script>

<style scoped>
@media (min-width: theme(--breakpoint-sm)) {
    .position-list-split-body {
        overflow-x: auto;
    }

    .position-list-split-table {
        min-width: 82rem;
    }

}
</style>

<style scoped>
.position-pin-marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #dc2626;
    color: #ffffff;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 2rem;
}

.position-pin-marker-icon {
    width: 0.9375rem;
    height: 0.9375rem;
    color: #ffffff;
}

.table-listview-l-td.position-list-title-cell {
    padding: 0 !important;
    position: relative;
    overflow: hidden;
}

.position-title-cell-content {
    display: flex;
    align-items: flex-start;
    min-width: 0;
    padding: 0.25rem 1.25rem;
    min-height: 4.5rem;
}

.position-title-cell-content--pinned {
    padding-left: 3.25rem;
}

.position-process-step-tooltip-item {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 24px;
    padding: 4px 0;
}

.position-process-step-tooltip-item:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 15px;
    bottom: -8px;
    width: 1px;
    background: var(--ui-border-default);
    z-index: 1;
}

.position-process-step-tooltip-dot {
    position: relative;
    z-index: 2;
    margin-right: 9px;
    flex-shrink: 0;
    border-radius: 9999px;
}

.position-process-step-tooltip-text {
    display: block;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    line-height: 16px;
    color: var(--ui-text-secondary);
}

</style>
